import { type FormEvent, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimeCard from "@/components/feature/AnimeCard";
import NewsCard from "@/components/feature/NewsCard";
import EmptyState from "@/components/feature/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { hotAnime, searchAnime, searchNews } from "@/utils/content";

const hotKeywords = ["星海列车", "奇幻", "治愈", "机甲", "2025", "资讯", "赛博朋克"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const keyword = searchParams.get("q") || "";
  const [input, setInput] = useState(keyword);
  const [tab, setTab] = useState<"anime" | "news">("anime");

  usePageMeta({
    title: keyword ? `“${keyword}”的搜索结果 - 58动漫` : "搜索结果 - 58动漫",
    description: keyword
      ? `58动漫为你找到与“${keyword}”相关的动漫作品与资讯，支持在动漫作品与资讯之间切换查看。`
      : "在 58动漫搜索动漫作品与资讯，快速定位你想看的动画与行业动态。",
    keywords: `${keyword},动漫搜索,作品搜索,资讯搜索,58动漫`,
  });

  const animeResults = useMemo(() => (keyword ? searchAnime(keyword) : []), [keyword]);
  const newsResults = useMemo(() => (keyword ? searchNews(keyword) : []), [keyword]);
  const total = animeResults.length + newsResults.length;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const q = input.trim();
    if (!q) return;
    setSearchParams({ q });
    setTab("anime");
  };

  const jumpToKeyword = (word: string) => {
    setInput(word);
    setSearchParams({ q: word });
    setTab("anime");
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-8 md:py-8 xl:px-12">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

      <div className="mt-4 max-w-3xl">
        <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          {keyword ? `“${keyword}”的搜索结果` : "站内搜索"}
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          搜索覆盖动漫作品与资讯，支持关键词的包含式模糊匹配。
        </p>

        <form onSubmit={submit} className="mt-5">
          <div className="flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 p-2 transition-colors focus-within:border-primary-300">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-lg text-foreground-400">
              <i className="ri-search-line" />
            </span>
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              type="search"
              placeholder="输入动漫名称、类型或资讯关键词"
              aria-label="搜索关键词"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="shrink-0 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </div>
        </form>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-foreground-500">热门搜索：</span>
          {hotKeywords.map((word) => (
            <button
              key={word}
              type="button"
              onClick={() => jumpToKeyword(word)}
              className="whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600 transition-colors hover:bg-primary-50 hover:text-primary-600"
            >
              {word}
            </button>
          ))}
        </div>
      </div>

      {keyword && (
        <>
          <div className="mt-7 flex flex-wrap items-center gap-2 border-b border-background-200">
            {[
              { key: "anime" as const, label: "动漫作品", count: animeResults.length },
              { key: "news" as const, label: "资讯", count: newsResults.length },
            ].map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setTab(item.key)}
                className={`-mb-px flex items-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                  tab === item.key
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-foreground-500 hover:text-foreground-800"
                }`}
              >
                {item.label}
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[11px] ${
                    tab === item.key ? "bg-primary-100 text-primary-700" : "bg-background-100 text-foreground-500"
                  }`}
                >
                  {item.count}
                </span>
              </button>
            ))}
            <span className="ml-auto pb-2.5 text-xs text-foreground-500">
              共 {total} 条结果
            </span>
          </div>

          <div className="mt-6">
            {tab === "anime" ? (
              animeResults.length > 0 ? (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {animeResults.map((item) => (
                    <AnimeCard key={item.id} anime={item} />
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon="ri-emotion-sad-line"
                  title={`没有找到与“${keyword}”相关的动漫作品`}
                  description="换个关键词试试，或者切换到资讯分类查看。"
                  actionLabel="查看资讯结果"
                  onAction={() => setTab("news")}
                />
              )
            ) : newsResults.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {newsResults.map((item) => (
                  <NewsCard key={item.id} news={item} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon="ri-emotion-sad-line"
                title={`没有找到与“${keyword}”相关的资讯`}
                description="换个关键词试试，或者切换到动漫作品分类查看。"
                actionLabel="查看动漫作品"
                onAction={() => setTab("anime")}
              />
            )}
          </div>
        </>
      )}

      <section className="mt-12" aria-label="热门推荐">
        <h2 className="mb-5 font-heading text-lg font-extrabold text-foreground-950">
          {keyword && total === 0 ? "换个方向，看看这些热门作品" : "猜你想看"}
        </h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {hotAnime(4).map((item) => (
            <AnimeCard key={item.id} anime={item} />
          ))}
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/category/最新"
            className="flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-compass-3-line text-base" />
            浏览全部作品
          </Link>
          <button
            type="button"
            onClick={() => navigate("/news")}
            className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-newspaper-line text-base" />
            查看最新资讯
          </button>
        </div>
      </section>
    </div>
  );
}